import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Loading from './Loading';

const ExhibitorProfile = ({
  exhibitorId,
  conventionId,
  onClose,
  isLargeScreen,
}) => {
  const [exhibitor, setExhibitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!exhibitorId) return;

    const fetchExhibitor = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `/api/v1/exhibitor_profiles/${exhibitorId}`,
          {
            params: { c: conventionId },
          },
        );
        setExhibitor(response.data);
      } catch (err) {
        setError(
          err.response?.data?.error || 'Failed to load exhibitor profile',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchExhibitor();
  }, [exhibitorId, conventionId]);

  const getExhibitorTypeIcon = type => {
    switch (type) {
      case 'Artists':
        return 'fa-paintbrush';
      case 'Authors':
        return 'fa-book';
      case 'Exhibitors':
      default:
        return 'fa-exhibit-booth';
    }
  };

  const handleLinkClick = (linkType, url) => {
    // Analytics tracking
    if (typeof gtag === 'function') {
      gtag('event', 'exhibitor_link_click', {
        exhibitor_name: exhibitor.name,
        exhibitor_id: exhibitor.id,
        link_type: linkType,
        link_url: url,
      });
    }
  };

  if (loading) {
    return (
      <div className="profile-card loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-card error">
        <div className="error-message">
          <h3>Error Loading Profile</h3>
          <p>{error}</p>
          <button onClick={onClose} className="button">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!exhibitor) {
    return (
      <div className="profile-card error">
        <div className="error-message">
          <h3>Exhibitor Not Found</h3>
          <button onClick={onClose} className="button">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-card">
      <section id="card-wrapper">
        <section id="main">
          {!isLargeScreen && (
            <div className="profile-header">
              <button className="close-button" onClick={onClose}>
                <i className="fa fa-arrow-left"></i>
                <span>Back to List</span>
              </button>
            </div>
          )}
          
          <h1 id="title-exhibitor">{exhibitor.name}</h1>
          <div id="exhibitor-type">
            <i
              className={`fa ${getExhibitorTypeIcon(exhibitor.exhibitorType)}`}
            ></i>
            {exhibitor.exhibitorType}
          </div>

          <div className="flex">
            <div id="avatar-container">
              {exhibitor.avatarUrl && (
                <img
                  className="avatar no-border"
                  src={exhibitor.avatarUrl}
                  alt={`${exhibitor.name} logo`}
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>

            <div>
              <div id="exhibitor-links">
                {exhibitor.location &&
                  exhibitor.location.label &&
                  exhibitor.location.navigateTo && (
                    <div className="events">
                      <a
                        className="tracklink"
                        href={exhibitor.location.navigateTo}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          handleLinkClick(
                            'location',
                            exhibitor.location.navigateTo,
                          )
                        }
                      >
                        <i className="fa fa-map-marker"></i>
                        <span>{exhibitor.location.label}</span>
                      </a>
                    </div>
                  )}

                {exhibitor.genConEvents && (
                  <div className="events">
                    <a
                      className="tracklink"
                      href={exhibitor.genConEvents.navigateTo}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLinkClick(
                          'events',
                          exhibitor.genConEvents.navigateTo,
                        )
                      }
                    >
                      <i className="fa fa-ticket"></i>
                      <span>{exhibitor.genConEvents.label}</span>
                    </a>
                  </div>
                )}

                {exhibitor.website && (
                  <div className="website">
                    <a
                      className="tracklink"
                      href={exhibitor.website.navigateTo}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        handleLinkClick('website', exhibitor.website.navigateTo)
                      }
                    >
                      <i className="fa fa-external-link"></i>
                      <span>{exhibitor.website.label}</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {exhibitor.tags && exhibitor.tags.length > 0 && (
            <div id="tags">
              <ul>
                {exhibitor.tags.map((tag, index) => (
                  <li key={index}>{tag}</li>
                ))}
              </ul>
            </div>
          )}

          {exhibitor.description && (
            <div id="exhibitor-description">{exhibitor.description}</div>
          )}

          {exhibitor.images && exhibitor.images.length > 0 && (
            <div id="featured-images">
              {exhibitor.images.map((image, index) => (
                <a
                  key={index}
                  data-fslightbox={`gallery-${exhibitor.id}`}
                  data-type="image"
                  href={image.fullSizeImageUrl}
                >
                  <div className="featured-image">
                    <img
                      className="avatar no-border"
                      src={image.imageUrl}
                      alt={`${exhibitor.name} image ${index + 1}`}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </section>
    </div>
  );
};

export default ExhibitorProfile;
